import '../../Styles/Layout/FileCard.css'
import { TextFileRecord, ImageFileRecord } from "../../types/files";
import { AttachmentType, DocumentType } from "../../types/message";
import { useAttachmentContext } from "../../contexts/AttachmentContext";
import { Cancel01Icon } from 'hugeicons-react';
import { getCodeLanguageByExtension } from '../../hooks/FileHandler/utils';
import { FileCardThumbnail } from './FileCardThumbnail';
import { useState } from 'react';

const CircularLoading = () => {
    return (
      <div className="mini-loading">
        <div className="mini-loading-circle"></div>
      </div>
    );
};

const TYPE_LABELS: Record<string, string> = {
    pdf: 'PDF',
    doc: 'DOC',
    code: 'CODE',
};

/**
 * Label shown in the pill at the bottom of a file card — the code language
 * when we know it (e.g. "typescript"), otherwise the file type ("PDF"/"DOC").
 */
export const getFileTypeLabel = (
    name: string,
    type: AttachmentType,
    codeLanguage?: string
): string => {
    if (type === 'code') {
        const language = codeLanguage || getCodeLanguageByExtension(name);
        if (language) return language;
    }
    return TYPE_LABELS[type] ?? 'FILE';
};

interface FileCardProps {
    file: TextFileRecord | ImageFileRecord;
    onRemove?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const FileCard = ({ file, onRemove }: FileCardProps) => {
    const { removeTextFile, handleFileCardClick, handleUploadedFileClick } = useAttachmentContext();
    const [isRemoving, setIsRemoving] = useState(false);
    const isLoaded = file.isLoaded;

    const content = 'content' in file ? file.content : null;
    const codeLanguage = 'codeLanguage' in file ? file.codeLanguage : undefined;
    const label = getFileTypeLabel(file.name, file.type, codeLanguage);

    const handleClick = () => {
        if (!isLoaded) return;
        if ('content' in file) handleFileCardClick(file);
        else handleUploadedFileClick(file);
    }


    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setIsRemoving(true);

        setTimeout(() => {
            if (onRemove) {
                onRemove(e);
            } else {
                removeTextFile(e, file.id);
            }
        }, 400);
    }

    return (
        <div className={`file-card-wrapper ${isRemoving ? 'removing' : ''}`}>
            <div
                className="file-card"
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleClick();
                    }
                }}
                aria-label={`${file.name}, ${label}${isLoaded ? '' : ', loading'}`}
            >
                {!isLoaded ? (
                    <div className="file-card-loading">
                        <CircularLoading />
                    </div>
                ) : (
                    <FileCardThumbnail
                        type={file.type as DocumentType}
                        content={content}
                        url={file.url}
                    />
                )}

                <span className="file-card-type">{label}</span>

                <button
                    type="button"
                    onClick={handleRemove}
                    className="file-card-remove"
                    aria-label="Remove File"
                >
                    <Cancel01Icon size={13} strokeWidth={2.5} />
                </button>
            </div>
        </div>
    )
}
